import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Styles from './grouppolicyStyle.module.scss';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { LoaderCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api, { noBrowserCache } from '../../components/services/apiService';
import { usePermissions } from '../../hooks/usePermissions';

interface GroupItem {
  groupId?: string;
  groupName: string;
  maintenanceTime?: string;
  reboot?: boolean;
  agentCount?: number;
  isActive?: boolean;
}

const GroupList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'Group' | 'Automation'>('Group');
  const [group, setGroup] = useState<GroupItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();

  const { perms, canEdit } = usePermissions();
  const canEditGroups = canEdit('groupPolicies');
  const canViewGroups = !!perms.groupPolicies; // any assigned level (view/create/...) can at least view details
  const hasActionColumn = canEditGroups || canViewGroups;
  const tableGridTemplate = hasActionColumn ? '3fr 2fr 2fr 2fr' : '3fr 2fr 2fr';

  const CreateAutomation = () => navigate('/GroupPolicy');

  const getGroup = async () => {
    try {
      setLoading(true);
      const tenantId = selectedTenantId;

      // Call groups API (all groups)
      const groupsResponse = await api.get(
        `${Project.api}tenantservice/TenantGroup/${tenantId}/groups?includeInactive=true`,
        noBrowserCache(),
      );
      if (groupsResponse?.data?.data?.length) {
        const groups = groupsResponse.data.data; // Array of tenant objects
        // Flatten tenantGroupList arrays
        const allGroups = groups.flatMap((tenant: any) => tenant.tenantGroupList || []);

        // ✅ Find the default group
        const defaultGroup = allGroups.find((group: any) => group.isDefault === true);

        if (defaultGroup?.groupName) {
          // Store only the group name in localStorage
          localStorage.setItem('defaultTenantGroupName', defaultGroup.groupName);
        } else {
          // If no default group found, clear previous entry
          localStorage.removeItem('defaultTenantGroupName');
        }
      }

      const allGroups = groupsResponse?.data?.data?.[0]?.tenantGroupList ?? [];

      // Map groups with agent counts from the groups API response
      const mergedGroups = allGroups.map((g: any) => ({
        groupId: g.tenantGroupId,
        groupName: g.groupName,
        agentCount: g.agentCount ?? 0,
        isActive: g.isActive,
      }));

      setGroup(mergedGroups);
    } catch (error) {
      console.error(`Error fetching groups:`, error);
      setGroup([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (tenantGroupId: string) => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        // console.log('tenantGroupId', tenantGroupId);
        await api.put(`${Project.api}tenantservice/TenantGroup/${tenantId}/disable-tenant-group/${tenantGroupId}`, '');

        getGroup();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };
  useEffect(() => {
    getGroup();
  }, []);

  return (
    <div style={{ padding: '1rem', background: '#111', color: '#fff', fontFamily: 'Arial, sans-serif' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        {['Group', 'Automation'].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab as 'Group' | 'Automation');
              if (tab === 'Automation') CreateAutomation();
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              borderRight: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
              borderLeft: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {tab}
          </button>
        ))}
        {canEditGroups && (
          <button
            className={Styles['automationBtn']}
            style={{ marginLeft: 'auto', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => navigate('/groupPolicy/CreateGroup')}
          >
            Create Group
          </button>
        )}
      </div>

      {/* Table Container */}
      <div
        style={{
          background: '#1e1e1e',
          borderRadius: '8px',
          overflow: 'hidden',
          marginTop: '20px',
          padding: '20px',
          minHeight: '200px',
        }}
      >
        {/* Table Head */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: tableGridTemplate,
            marginBottom: '10px',
            whiteSpace: 'nowrap',
            padding: '0 1rem',
            alignItems: 'center',
          }}
          className={Styles['tableHead']}
        >
          <div>Group Name</div>
          <div style={{ justifySelf: 'center' }}>Number of Devices</div>
          <div style={{ justifySelf: 'center' }}>Status</div>
          {hasActionColumn && <div style={{ justifySelf: 'center' }}>Actions</div>}
        </div>

        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: '#1D1D1D',
                color: '#FEFEFE',
                padding: '2rem',
                borderRadius: '30px',
                width: '90%',
                maxWidth: '500px',
                textAlign: 'center',
              }}
            >
              <img src="/images/delete-icon.svg" alt="delete" style={{ marginLeft: '200px', marginBottom: '1rem' }} />
              <h5>Are you sure you want to delete?</h5>
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#1D1D1D',
                    border: '1px solid #FEFEFE',
                    borderRadius: '30px',
                    cursor: 'pointer',
                    marginRight: '30px',
                  }}
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#D12929',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '30px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (selectedGroupId) {
                      deleteGroup(selectedGroupId);
                    }
                    setShowDeleteModal(false);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loader or No Data */}
        {loading ? (
          <div className="py-12 text-center">
            <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
          </div>
        ) : group.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No records found</div>
        ) : (
          group.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: tableGridTemplate,
                padding: '1rem',
                borderBottom: '1px solid #2a2a2a',
                background: idx % 2 === 0 ? '#161617' : '#232222',
                alignItems: 'center',
              }}
            >
              <div>{item.groupName}</div>
              <div style={{ justifySelf: 'center' }}>{item.agentCount ?? 0}</div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span
                  className={`px-2 py-1 ${
                    item.isActive === true
                      ? 'bg-green-900/30 text-green-400 rounded-md text-sm font-medium'
                      : item.isActive === false
                        ? 'bg-red-900/30 text-red-400 rounded-md text-sm font-medium'
                        : 'bg-gray-900/30 text-gray-400 rounded-md text-sm font-medium'
                  }`}
                >
                  {item.isActive ? 'Active' : 'Disabled'}
                </span>
              </div>

              {hasActionColumn && (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button
                    title={canEditGroups ? 'Edit' : 'View'}
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                    onClick={() => navigate(`/groupPolicy/CreateGroup/${item.groupId}`)}
                  >
                    {canEditGroups ? <FaEdit /> : <FaEye />}
                  </button>
                  {/* <button
                    title="Delete"
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                    onClick={() => {
                      setSelectedGroupId(item.groupId);
                      setShowDeleteModal(true);
                    }}
                  >
                    <FaTrash />
                  </button> */}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GroupList;
